import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import DatePickerMui from '../../../components/DatePickerMui';
import styles from './styles';
import { getOfferExpiryDateValues } from '../service/CreatePromotionService';

const OfferExpiryDate = ({
  showDatePickerForRegOfferID,
  setShowDatePickerForRegOfferID,
  handleOfferExpiryDate,
  handleOfferExpiryDateForInstallation,
  handleScheduleExpDate,
  handlePurchaseExpDate,
  promoExpiry,
  offerApplicable,
  defaultExpiryDate,
  promotionData,
  isDateSelectionDisabled,
  disableConfig,
}) => {
  // Get all date values and constraints from the service function
  const {
    expiryDateValue,
    installationDateValue,
    consultationDateValue,
    purchaseDateValue,
    minDate,
    maxDate,
  } = getOfferExpiryDateValues({
    offerApplicable,
    promoExpiry,
    promotionData,
    defaultExpiryDate,
  });

  // Track consultation date and purchase date locally
  const [localConsultationDate, setLocalConsultationDate] = useState(null);
  const [localPurchaseDate, setLocalPurchaseDate] = useState(null);

  // Initialize local state when values change from props
  useEffect(() => {
    if (consultationDateValue) {
      setLocalConsultationDate(new Date(consultationDateValue));
    }
  }, [consultationDateValue]);

  useEffect(() => {
    if (purchaseDateValue) {
      setLocalPurchaseDate(new Date(purchaseDateValue));
    }
  }, [purchaseDateValue]);

  // Reset local dates when disableConfig changes to true
  useEffect(() => {
    if (disableConfig) {
      setLocalConsultationDate(null);
      setLocalPurchaseDate(null);
    }
  }, [disableConfig, handleScheduleExpDate, handlePurchaseExpDate]);

  // Custom handler for consultation date change
  const customHandleScheduleExpDate = (date) => {
    // Only allow selecting consultation date if purchase date is not set
    // or if consultation date is before purchase date
    if (!localPurchaseDate || (date && new Date(date) <= localPurchaseDate)) {
      setLocalConsultationDate(date ? new Date(date) : null);
      handleScheduleExpDate(date);
    }
  };

  // Custom handler for purchase date change
  const customHandlePurchaseExpDate = (date) => {
    setLocalPurchaseDate(date ? new Date(date) : null);
    handlePurchaseExpDate(date);
  };

  return (
    <div style={{ padding: '0 20px 20px' }}>
      <Typography sx={styles.headingSmall}>Offer expiry date</Typography>
      <RadioGroup
        aria-labelledby="discount-offer-radio-buttons-group-label"
        value={showDatePickerForRegOfferID}
        name="discount-offer-radio-buttons-group"
        onChange={(e) => setShowDatePickerForRegOfferID(parseInt(e.target.value))}
      >
        <div className="offer-expiry-date">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel value={1} control={<Radio />} />
            {showDatePickerForRegOfferID === 1 ? (
              <Typography>
                Offer expires on
                <DatePickerMui
                  style={{ ...styles.datePickerPosition, paddingLeft: '5px' }}
                  defaultValue={null}
                  updateDateValue={expiryDateValue}
                  handleDateChange={handleOfferExpiryDate}
                  minDate={minDate}
                  maxDate={maxDate}
                  disabled={isDateSelectionDisabled}
                />
              </Typography>
            ) : (
              <Typography sx={{ color: 'text.disabled' }}>Offer expires on mm/dd/yy.</Typography>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel value={2} control={<Radio />} />
          {showDatePickerForRegOfferID === 2 ? (
            <Typography>
              To be eligible for advertised offers, installation must be scheduled to take place on
              or before{' '}
              <DatePickerMui
                style={{ ...styles.datePickerPosition, paddingLeft: '5px' }}
                defaultValue={null}
                handleDateChange={handleOfferExpiryDateForInstallation}
                updateDateValue={installationDateValue}
                minDate={minDate}
                disabled={isDateSelectionDisabled}
              />
            </Typography>
          ) : (
            <Typography sx={{ color: 'text.disabled' }}>
              To be eligible for advertised offers, installation must be scheduled to take place on
              or before mm/dd/yy.
            </Typography>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel value={3} control={<Radio />} />
          {showDatePickerForRegOfferID === 3 ? (
            <div>
              <p style={{ display: 'inline', lineHeight: '2' }}>
                To be eligible for advertised offers, in-home consultation must be scheduled by
              </p>{' '}
              <DatePickerMui
                className={'scheduleExpDate'}
                style={{ ...styles.datePickerPosition, paddingRight: '5px', paddingTop: '8px' }}
                defaultValue={null}
                handleDateChange={customHandleScheduleExpDate}
                updateDateValue={consultationDateValue}
                minDate={minDate}
                maxDate={localPurchaseDate || undefined}
                disabled={isDateSelectionDisabled}
              />
              <p style={{ display: 'inline' }}> and purchase must be made by </p>
              <DatePickerMui
                className={'purchaseExpDate'}
                style={{ ...styles.datePickerPosition, paddingTop: '8px' }}
                defaultValue={null}
                handleDateChange={customHandlePurchaseExpDate}
                updateDateValue={purchaseDateValue}
                minDate={localConsultationDate || minDate}
                disabled={isDateSelectionDisabled}
              />
            </div>
          ) : (
            <Typography sx={{ color: 'text.disabled' }}>
              To be eligible for advertised offers, in-home consultation must be scheduled by
              mm/dd/yy and purchase must be made by mm/dd/yy.
            </Typography>
          )}
        </div>
      </RadioGroup>
    </div>
  );
};

// PropTypes validation for the component
OfferExpiryDate.propTypes = {
  showDatePickerForRegOfferID: PropTypes.number.isRequired,
  setShowDatePickerForRegOfferID: PropTypes.func.isRequired,
  handleOfferExpiryDate: PropTypes.func.isRequired,
  handleOfferExpiryDateForInstallation: PropTypes.func.isRequired,
  handleScheduleExpDate: PropTypes.func.isRequired,
  handlePurchaseExpDate: PropTypes.func.isRequired,
  promoExpiry: PropTypes.object,
  offerApplicable: PropTypes.bool,
  defaultExpiryDate: PropTypes.instanceOf(Date),
  isDateSelectionDisabled: PropTypes.bool,
  disableConfig: PropTypes.bool,
  promotionData: PropTypes.shape({
    status: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
  }),
};

OfferExpiryDate.defaultProps = {
  promoExpiry: {},
  offerApplicable: false,
  promotionData: {},
};

export default OfferExpiryDate;
